import { z } from 'zod';
import assertUnreachable from '../../utils/assertUnreachable';
import { ToolNameSchema } from './schemas/visualizations';
export * from './adminTypes';
export * from './aiEvalAssessment';
export * from './chartConfig/slack';
export * from './chartConfig/web';
export * from './constants';
export * from './coder';
export * from './dashboardContext';
export * from './aiAgentReviewClassifierTypes';
export * from './documentTypes';
export * from './filterExploreByTags';
export * from './followUpTools';
export * from './projectContext';
export * from './requestTypes';
export * from './schemas';
export * from './schemas/agentReadiness';
export * from './schemas/agentSuggestions';
export * from './types';
export * from './utils';
export * from './validators';
export const baseAgentSchema = z.object({
    uuid: z.string(),
    projectUuid: z.string(),
    organizationUuid: z.string(),
    name: z.string(),
    description: z.string().nullable(),
    imageUrl: z.string().url().nullable(),
    imageUrlSource: z.enum(['upload', 'url']).nullable(),
    tags: z.array(z.string()).nullable(),
    integrations: z.array(z.object({
        type: z.literal('slack'),
        channelId: z.string(),
    })),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
    instruction: z
        .string()
        .max(8192, // 8kb
    'Custom instruction is too long. Maximum allowed is 8,100 characters.')
        .nullable(),
    provider: z.string(),
    model: z.string(),
    groupAccess: z.array(z.string()),
    userAccess: z.array(z.string()),
    spaceAccess: z.array(z.string()),
    enableDataAccess: z.boolean(),
    enableSelfImprovement: z.boolean(),
    enableContentTools: z.boolean(),
    enableUserContext: z.boolean(),
    adminOnly: z.boolean(),
    modelConfig: z.custom().nullable(),
    version: z.number(),
});
// An empty spaceAccess list means the agent is unrestricted (all spaces).
export const hasAiAgentAccessToSpace = (agent, spaceUuid) => agent.spaceAccess.length === 0 || agent.spaceAccess.includes(spaceUuid);
export const AI_AGENT_MEMORY_CONSOLIDATION_OPERATION_TYPES = ['merge', 'supersede', 'retire'];
/**
 * Every existing row an operation names — a merge's sources included. Validation
 * and the apply transaction's locking share it, so the set of rows an operation
 * is checked against can never drift from the set it locks.
 */
export const getAiAgentMemoryConsolidationOperationSlugs = (operation) => {
    switch (operation.type) {
        case 'merge':
            return operation.source_slugs;
        case 'supersede':
            return [operation.loser_slug, operation.winner_slug];
        case 'retire':
            return [operation.slug];
        default:
            return assertUnreachable(operation, 'Unknown consolidation op');
    }
};
/** Enumerable and content-free, so both the run audit and metrics can label by it. */
export const AI_AGENT_MEMORY_CONSOLIDATION_REJECTION_REASONS = [
    'unknown_slug',
    'duplicate_target',
    'self_supersede',
    'insufficient_sources',
    'row_moved',
];
/** Identifies the path that started a consolidation run. */
export const AI_AGENT_MEMORY_CONSOLIDATION_TRIGGERS = [
    'scheduled',
    'manual',
];
// The hosted GitHub MCP server. The one-click "Connect GitHub" flow points the
// MCP server here and authenticates with the org's GitHub App installation
// token (minted server-side), so no separate OAuth/PAT step is needed.
export const GITHUB_MCP_SERVER_URL = 'https://api.githubcopilot.com/mcp/';
export const GITHUB_MCP_SERVER_NAME = 'GitHub';
export const isAiAgentSqlArtifactVizQuery = (query) => query.source === 'sql';
export const isAiAgentMcpToolName = (toolName) => toolName.startsWith('mcp_');
export const isAiAgentToolName = (toolName) => ToolNameSchema.safeParse(toolName).success ||
    isAiAgentMcpToolName(toolName);
export const isAiSqlChartArtifactConfig = (config) => typeof config === 'object' &&
    config !== null &&
    'source' in config &&
    config.source === 'sql' &&
    'sql' in config &&
    typeof config.sql === 'string' &&
    'limit' in config &&
    typeof config.limit === 'number';
/**
 * Type guard to check if a CreateEvaluationPrompt is a string prompt
 */
export const isStringPrompt = (prompt) => 'prompt' in prompt;
/**
 * Type guard to check if a CreateEvaluationPrompt is a thread reference
 */
export const isThreadPrompt = (prompt) => 'promptUuid' in prompt && 'threadUuid' in prompt;
//# sourceMappingURL=index.js.map